import React, { useEffect, useMemo, useState } from "react";
import MUITable from "../../../common/components/mui-table/mui-table";
import {
  AlertComponent,
  BreadcrumbComponent,
  SearchInputComponent,
} from "../../../common/components";
import { Badge, Box, Typography } from "@mui/material";
import Button from "../../../common/components/button/button";
import FilterIcon from "../../../assets/icons/filter-icon.svg";
import AlertIcon from "../../../assets/icons/alert-icon.svg";
import AddNewTenant from "../add-tenant/add-new-tenant";
import "./tenant-list.scss";
import {
  getTenets,
  resendPaymentLink,
  updateTenetStatus,
} from "../../../api/tenets";
import { useSetLoading } from "../../../common/hooks/appearance";
import {
  breadcrumbLink,
  confirmationDefaultValues,
  ConfirmationPopupTypes,
  fetchUniqueCompanyNames,
  SortOption,
  TenentList,
  TenetListParams,
} from "./tenant-list-helper";
import PaginationComponent from "../../../common/components/pagination-component/pagination-component";
import _ from "lodash";
import MODULES from "../../../constants/modules";
import { useResourcePermissions } from "../../../common/hooks/resource-permissions";
import ConfirmationPopup from "../../../common/components/confirmation-popup/confirmation-popup";
import TenantFilterOptions from "./tenant-filter-options/tenant-filter-options";
import { useNavigate } from "react-router-dom";
import { routes } from "../../../constants/routes";
import icons from "../../../assets/icons/icons";
import { ROLES, SUPER_TENANT } from "../../../utils/constants";
import CustomTooltip from "../../../common/components/mui-table/custom-tooltip";

const TenetList: React.FC = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(15);
  const [searchText, setSearchText] = useState<string>("");
  const [confirmationModal, setConfirmationModal] =
    useState<ConfirmationPopupTypes>(confirmationDefaultValues);
  const [sortOption, setSortOption] = useState<SortOption>({
    sortDirection: null,
    sortBy: null,
  });
  const [alert, setAlert] = useState<{
    isOpen?: boolean;
    message?: any;
    type?: string;
  }>({});
  const [tenantList, setTenantList] = useState<TenentList>({
    totalRecords: null,
    data: [],
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [companyNames, setCompanyNames] = useState<string[]>([]);

  const { canWrite, permissions } = useResourcePermissions(
    MODULES.PARTNER_LIST
  );

  const handleResendLinkToPertner = async (tenantId: any) => {
    try {
      setIsLoading(true);
      const resp = await resendPaymentLink(tenantId);
      setConfirmationModal(confirmationDefaultValues);
      setAlert({
        isOpen: true,
        message: resp?.data?.data?.message || "Resent payment link",
        type: "success",
      });

      setIsLoading(false);
    } catch (err: any) {
      setAlert({
        isOpen: true,
        message:
          err?.response?.data?.message ||
          err?.message ||
          err?.error ||
          "Failed to Resent payment link",
        type: "error",
      });
      setIsLoading(false);
    }
  };

  const handleClickEmailLink = (row: any) => {
    setConfirmationModal({
      ...confirmationDefaultValues,
      isOpen: true,
      title: `Resend Link`,
      partnerDetails: row,
      updatedPartnerDataList: {},
      note: `Do you want to resend the payment link to this Partner?`,
      value: row?.id,
      buttonText: "Resend Link",
      Icon: icons.AlertInfoIcon,
    });
  };

  const headers = useMemo(
    () => [
      {
        label: "Company Name",
        key: "company_name",
        isSortable: true,
        valueGetter: (row: any) => {
          if (row?.tenantSubscriptionPayments?.length) {
            return (
              <>
                <Box className="payment-pending-table-cell">
                  <span onClick={() => handleTableCellClick(row)}>
                    <CustomTooltip title={row.company_name || ""} arrow>
                      {row.company_name}
                    </CustomTooltip>
                  </span>
                  <CustomTooltip title={"Resend Link"} arrow>
                    <Box
                      onClick={() => {
                        handleClickEmailLink(row);
                      }}
                      className="resend-link-wrapper"
                    >
                      <img src={icons.EmailResendIcons} alt="email-resend" />
                    </Box>
                  </CustomTooltip>
                </Box>
              </>
            );
          }
          return (
            <span
              onClick={() => handleTableCellClick(row)}
              className="company-name-span "
            >
              <CustomTooltip title={row.company_name || ""} arrow>
                {row.company_name}
              </CustomTooltip>
            </span>
          );
        },
        classNameGetter: (row: any) => {
          if (row?.tenantSubscriptionPayments?.length) {
            return "pending-payment-partner";
          }

          return "";
        },
      },

      { label: "Email", key: "email", isSortable: true, isOnClick: true },
      {
        label: "Phone",
        key: "phone_number",
        isSortable: true,
        isOnClick: true,
      },
      {
        label: "Timezone",
        key: "timezone",
        isSortable: true,
        isOnClick: true,
      },
      {
        label: "Status",
        key: "status",
        isSortable: true,
        isEditable: true,
        handleOptions: (row: any, options: any) => {
          if (!row?.tenantDbDetails?.id) {
            return [{ value: "INACTIVE", label: "Inactive" }];
          }
          return options;
        },
        isDisabled: (row: any) => {
          const storedData = localStorage.getItem("loginData");
          if (!canWrite) {
            return true;
          }
          if (storedData) {
            const loginData = JSON.parse(storedData);
            const { userDetails, tenantDetails } = loginData;
            return (
              userDetails?.roles?.role !== ROLES.SUPER_ADMIN &&
              tenantDetails?.tenantType !== SUPER_TENANT
            );
          }
          return row?.tenant_type === SUPER_TENANT;
        },
      },
    ],
    []
  );

  const handleSort = (column: string, sortType: string) => {
    let sortValues: SortOption = { sortBy: null, sortDirection: null };
    if (sortType !== "Unsort") {
      sortValues = { sortDirection: sortType.toLowerCase(), sortBy: column };
    }
    setCurrentPage(1);
    setSortOption(sortValues);
  };

  const getTenentList = async (page: number = 1, search: string = "") => {
    try {
      setIsLoading(true);

      const offset = (page - 1) * pageSize;
      let params: TenetListParams = {
        limit: pageSize,
        offset: offset,
      };
      if (sortOption?.sortBy) {
        params = { ...params, ...sortOption };
      }
      if (search) {
        params.searchText = search;
      }
      if (Object.keys(filters).length) {
        params = { ...params, ...filters };
      }
      const response = await getTenets(params);
      setTenantList(response?.data?.data?.data);
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      console.error(e);
    }
  };

  const handlePartnerStatus = async (partnerId: number, payload: any) => {
    try {
      setIsLoading(true);
      const res = await updateTenetStatus(partnerId, payload);
      setTenantList((prev: any) => ({
        ...prev,
        data: confirmationModal?.updatedPartnerDataList,
      }));
      setConfirmationModal(confirmationDefaultValues);
      setAlert({
        isOpen: true,
        message: "Partner status updated successfully.",
        type: "success",
      });
      setIsLoading(false);
    } catch (err: any) {
      setAlert({
        isOpen: true,
        message:
          err?.response?.data?.message ||
          err?.message ||
          err?.error ||
          "Failed to update status.",
        type: "error",
      });
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setConfirmationModal(confirmationDefaultValues);
  };
  const handleOnOkay = () => {
    if (confirmationModal?.title === "Resend Link") {
      handleResendLinkToPertner(confirmationModal?.value);
    } else {
      const payload = { status: confirmationModal?.value };
      const partnerId = confirmationModal?.partnerDetails?.id;

      handlePartnerStatus(partnerId, payload);
    }
  };

  const toggle = () => {
    setIsOpen(false);
    setCurrentPage(1);
  };

  const handleSearch = _.debounce((text: string) => {
    setSearchText(text);
    setCurrentPage(1);
  }, 500);

  useEffect(() => {
    if (!isOpen) {
      getTenentList(currentPage, searchText);
    }
  }, [currentPage, searchText, isOpen, sortOption, filters]);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
  };

  const showLoading = useMemo(() => isLoading, [isLoading]);
  useSetLoading(showLoading);

  const onHandleChange = (rowIndex: number, key: string, value: string) => {
    const partnerDataList = [...tenantList?.data];
    partnerDataList[rowIndex] = {
      ...partnerDataList[rowIndex],
      [key]: value,
    };
    const partnerDetails: any = tenantList?.data?.[rowIndex];
    const isActive = value === "ACTIVE";
    const statusText = isActive ? "Active" : "Inactive";
    const noteText = isActive
      ? "Active will allow the partner user to use the application."
      : "Inactive will prevent the partner user from logging in to the application.";

    setConfirmationModal({
      ...confirmationDefaultValues,
      isOpen: true,
      title: `Are you sure you want to make the partner ${statusText}?`,
      partnerDetails: partnerDetails,
      updatedPartnerDataList: partnerDataList,
      note: `Note: Changing the status to ${noteText}`,
      value: value,
    });
  };

  // Handle the click event for the table cell
  const handleTableCellClick = (row: any) => {
    if (!row?.tenantDbDetails?.id) {
      setAlert({
        isOpen: true,
        message: (
          <Typography sx={{ fontSize: "13px" }}>
            <span style={{ fontWeight: "bold", fontSize: "16px" }}>
              Partner Onboard Pending.
            </span>
            <br />
            Please ensure payment is completed to access the partner view.
          </Typography>
        ),

        type: "error",
      });
    } else {
      navigate(routes.PARTNERDETAILS, {
        state: {
          partnerDetails: row,
          isFromPartnerList: true,
        },
      });
    }
  };

  const loadCompanies = async () => {
    setIsLoading(true);
    const companies = await fetchUniqueCompanyNames();
    setCompanyNames(companies);
    setIsLoading(false);
  };

  useEffect(() => {
    loadCompanies();
  }, []);

  const dropDownOptions = {
    status: [
      { value: "ACTIVE", label: "Active" },
      { value: "INACTIVE", label: "Inactive" },
    ],
  };

  const noResultsTxt = useMemo(() => {
    if (isLoading) {
      return "Loading partners...";
    }
    if (searchText) {
      return "No partners found matching your search criteria.";
    }
    return "No partners found";
  }, [isLoading, searchText]);

  const appliedFilterCount = useMemo(() => {
    let count = 0;
    const dateFilterActive = filters.onBoardedAtEnd || filters.onBoardedAtStart;
    const addressFilterActive = filters.country || filters.state || filters.city || filters.postcode;
    
    const otherFilters = { ...filters };
    delete otherFilters.onBoardedAtEnd;
    delete otherFilters.onBoardedAtStart;
    delete otherFilters?.country;
    delete otherFilters?.state;
    delete otherFilters?.city;
    delete otherFilters?.postcode;

    const otherActiveFilters = Object.values(otherFilters).filter(
      (val) =>
        val !== undefined &&
        val !== "" &&
        val !== null &&
        !(Array.isArray(val) && val.length === 0)
    ).length;

    if (dateFilterActive) {
      count += 1;
    }
    if (addressFilterActive) {
      count += 1;
    }

    count += otherActiveFilters;

    return count;
  }, [filters]);

  return (
    <>
      <div className="tenetlist-wrapper">
        <div className="breadcrumb-wrapper">
          <BreadcrumbComponent breadcrumbLink={breadcrumbLink} />
        </div>
        <div className="tenet-list-filter-wrapper">
          <div className="search-input-wrapper">
            <SearchInputComponent
              placeholder="Search by company name, email etc.,"
              onSearch={handleSearch}
            />
          </div>
          <div className="filter-right-section d-flex">
            <Badge
              badgeContent={appliedFilterCount}
              invisible={appliedFilterCount === 0}
              color="secondary"
              overlap="circular"
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              sx={{
                "& .MuiBadge-badge": {
                  top: "1px",
                  right: "14px",
                  transform: "scale(1)",
                },
              }}
            >
              <div
                className={`filter-icon ${isFilterOpen ? "active-filter" : ""}`}
                onClick={() => {
                  setIsFilterOpen(!isFilterOpen);
                  console.log(
                    "filters->",
                    !isFilterOpen,
                    filters,
                    companyNames
                  );
                }}
                style={{ cursor: "pointer" }}
              >
                <img src={FilterIcon} alt="filter-icon" />
              </div>
            </Badge>
            {canWrite ? (
              <Button
                label="Add New Partner"
                onClick={() => {
                  setIsOpen(true);
                }}
              />
            ) : (
              <></>
            )}
          </div>
        </div>
        <div className="tenant-table-filter">
          <Box className="tenet-list-table">
            <MUITable
              headers={headers}
              data={tenantList?.data}
              isStriped={false}
              onSort={handleSort}
              selectDropDownOptions={dropDownOptions}
              onHandleChange={onHandleChange}
              onSelectChange={onHandleChange}
              noResultsTxt={noResultsTxt}
              onColumnClick={handleTableCellClick}
              isShowPaymentNote={true}
            />
          </Box>
          {isFilterOpen ? (
            <TenantFilterOptions
              open={isFilterOpen}
              filters={filters}
              onClose={() => setIsFilterOpen(false)}
              companyNames={companyNames}
              onApplyFilters={(filterData, shouldClose = true) => {
                try {
                  setFilters(filterData);
                  if (shouldClose) setIsFilterOpen(false);
                  setCurrentPage(1);
                } catch (error: any) {
                  console.error("Failed to apply filters:", error);
                  setAlert({
                    isOpen: true,
                    message: "Failed to apply filters. Please try again.",
                    type: "error",
                  });
                }
              }}
            />
          ) : (
            <></>
          )}
        </div>

        {tenantList?.totalRecords ? (
          <div className="tenant-pagination-container">
            <PaginationComponent
              totalRecords={tenantList?.totalRecords || 0}
              pageSize={pageSize}
              currentPage={currentPage}
              onPageChange={handlePageChange}
            />
          </div>
        ) : (
          <></>
        )}
      </div>
      {isOpen && (
        <AddNewTenant open={isOpen} onClose={toggle} setAlert={setAlert} />
      )}
      {alert?.isOpen && (
        <AlertComponent
          message={alert?.message || ""}
          type={
            ["info", "success", "error", "warning"].includes(alert?.type || "")
              ? (alert?.type as "info" | "success" | "error" | "warning")
              : "info"
          }
          open={alert?.isOpen}
          onClose={() => setAlert({})}
        />
      )}

      {confirmationModal?.isOpen && (
        <ConfirmationPopup
          open={confirmationModal?.isOpen}
          title={confirmationModal?.title}
          note={confirmationModal?.note}
          buttonText={confirmationModal?.button}
          onCancel={handleCancel}
          onOkay={handleOnOkay}
          className={
            confirmationModal?.Icon
              ? "payment-pending-popup"
              : "partner-list-confirmation"
          }
          Icon={confirmationModal?.Icon ? confirmationModal?.Icon : AlertIcon}
        />
      )}
    </>
  );
};

export default TenetList;
